import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageLayout from "components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
    useLazyGetUserBiaDataQuery,
    useLazyGetUserBloodPressureDataQuery,
    useLazyGetUserECGListQuery,
    useLazyGetUserHrDataQuery,
    useLazyGetUserSp02DataQuery,
    useLazyGetUserStepsDataQuery,
    useLazyGetUserTempsDataQuery,
    useLazyGetUserVO2MaxDataQuery,
} from "./api/healthDataApi";
import { useGetUsersQuery } from "features/core/users/usersApiSlice";
import { useLazyGetHealthPatientDetailQuery } from "../patients/api/healthPatientsApiSlice";
import { HrHistoryGraph } from "./views/HrHistoryGraph";
import { HealthPatientCard } from "./views/HealthPatientCard";
import { StepsHistoryGraph } from "./views/StepsHistoryGraph";
import { HealthUserEvents } from "../events/components/HealthUserEvents";
import ECGMeasurements from "./components/ECGMeasurements";
import { Sp02HistoryGraph } from "./views/Sp02HistoryGraph";
import HealthMeasurementActions from "./components/HealthMeasurementActions";
import { TempHistoryGraph } from "./views/TempHistoryGraph";
import { BiaGraph } from "./views/BiaGraph";
import { BloodPressureHistoryGraph } from "./views/BloodPressureHistoryGraph";
import { useSelector } from "react-redux";
import { selectCompanyGuid, selectCurrentUserGuid } from "features/auth/authSlice";
import { Calendar } from "primereact/calendar";
import { VO2MaxHistoryGraph } from "./views/VO2MaxHistoryGraph";
import TopicSubscriber from "app/ws/TopicSubriber";

// ------------------------------------------
// Utility: non-mutating day range
// ------------------------------------------
function getDayRange(date) {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);

    const end = new Date(date);
    end.setHours(23, 59, 59, 999);

    return { start: start.getTime(), end: end.getTime() };
}



// ------------------------------------------
// Component (refactor A: minimal/conservative)
// ------------------------------------------
const HealthHistory = ({ privateOnly = false }) => {
    const [t] = useTranslation(["global", "health"]);
    const currentUserGuid = useSelector(selectCurrentUserGuid);
    const companyGuid = useSelector(selectCompanyGuid);

    const location = useLocation();
    const locationUserId = location.state?.userGUID;
    const locationDate = location.state?.date;

    const [userId, setUserId] = useState(privateOnly ? currentUserGuid : locationUserId || "");
    const [date, setDate] = useState(() => (locationDate ? new Date(locationDate * 1000) : new Date()));

    // lazy queries used individually by the component for success/fetching flags
    const [fetchHrData, { data: hrData, isFetching: hrIsFetching, isSuccess: hrIsSuccess }] = useLazyGetUserHrDataQuery();
    const [fetchSp02Data, { data: sp02Data, isFetching: sp02IsFetching, isSuccess: sp02IsSuccess }] = useLazyGetUserSp02DataQuery();
    const [fetchBPData, { data: bpData, isFetching: bpIsFetching, isSuccess: bpIsSuccess }] = useLazyGetUserBloodPressureDataQuery();
    const [fetchTempsData, { data: tempsData, isFetching: tempsIsFetching, isSuccess: tempsIsSuccess }] = useLazyGetUserTempsDataQuery();
    const [fetchStespData, { data: stepsData, isFetching: stepsIsFetching, isSuccess: stepsIsSuccess }] = useLazyGetUserStepsDataQuery();
    const [fetchVO2MaxData, { data: vO2MaxData, isFetching: vO2MaxIsFetching, isSuccess: vO2MaxIsSuccess }] = useLazyGetUserVO2MaxDataQuery();
    const [fetchBiaData, { data: biaData, isFetching: biaIsFetching, isSuccess: biaIsSuccess }] = useLazyGetUserBiaDataQuery();
    const [fetchPatient, { data: patient, isFetching: patientIsFetching, isSuccess: patientIsSuccess, reset: resetPatient }] = useLazyGetHealthPatientDetailQuery();
    const [fetchECGList, { data: ecgList, isSuccess: ecgListIsSuccess }] = useLazyGetUserECGListQuery();

    const { start, end } = useMemo(() => getDayRange(date), [date]);

    // ------------------------------------------
    // Hook: centralizza il fetch di tutti i dati
    // Minimal, mantiene gli stessi lazy hooks
    // ------------------------------------------



    const fetchAll = useCallback(() => {
        if (!userId) return;

        // patient fetch kept separate because original code called it with userId
        fetchPatient(userId);

        const params = { userGuid: userId, start, end };

        fetchHrData(params);
        fetchSp02Data(params);
        fetchBPData(params);
        fetchTempsData(params);
        fetchStespData(params);
        fetchVO2MaxData(params);
        fetchBiaData(params);
        fetchECGList(params);
    }, [userId, date]);


    // users list
    const { data: users, isSuccess: usersIsSuccess, isFetching: usersIsFetching, refetch: refetchUsers } = useGetUsersQuery();



    // handle refresh (used by button, TopicSubscriber, etc.)
    const handleRefresh = useCallback(() => {
        // conservative: keep original behavior to refetch users when no user selected
        if (!userId || userId === "") {
            refetchUsers();
            return;
        }

        // call centralized fetch
        fetchAll();
    }, [userId, date]);

    // initial fetch when userId changes or date changes
    useEffect(() => {
        if (userId) fetchAll()
    }, [userId, date]);


    // local helper to render select options
    const handleUserChange = (e) => {
        setUserId(e.target.value);
    };

    function isToday() {
        const today = new Date();

        // normalizza entrambe le date al giorno
        const isToday =
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate();
        return isToday
    }

    // page content (kept structure similar to original)
    const content = (
        <>
            <div className="row">
                {usersIsSuccess && !privateOnly && (
                    <div className="col-lg-6">
                        <div className="card bg-black bg-opacity-10  text-white p-4">
                            <div className="row align-items-center">
                                <div className="col-auto">{t("health:entity.patient.props.name")}</div>
                                <div className="col-auto">
                                    <select className="form-select" aria-label="Default select example" value={userId} onChange={handleUserChange}>
                                        <option value=""> {t("health:helper.select_patient")}</option>
                                        {users.map((user) => (
                                            <option key={user.guid} value={user.guid}>
                                                {user.displayName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="col-lg-6">
                    <div className="card bg-black bg-opacity-10  text-white p-4">
                        <div className="row align-items-center">
                            <div className="col-auto">Data:</div>
                            <div className="col-4">
                                <Calendar value={date} onChange={(e) => setDate(e.value)} dateFormat="dd/mm/yy" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col">
                {patientIsSuccess && (
                    <>
                        <HealthPatientCard patient={patient} />
                        <HealthMeasurementActions patient={patient} />
                        {
                            isToday() &&
                            <TopicSubscriber subTopic={`company/${companyGuid}/health/${userId}/sync`} onMessageReceived={handleRefresh} />
                        }

                    </>
                )}

                {patientIsSuccess && hrIsSuccess && hrData && hrData.length > 0 && (
                    <HrHistoryGraph
                        hrData={hrData}
                        hrThresholds={[
                            patient.profile.features.find((f) => f.name === "HR-SAFEZONE").settings.min,
                            patient.profile.features.find((f) => f.name === "HR-SAFEZONE").settings.max,
                        ]}
                        date={date}
                        onDateChange={setDate}
                    />
                )}

                {patientIsSuccess && vO2MaxIsSuccess && vO2MaxData && vO2MaxData.length > 0 && (
                    <VO2MaxHistoryGraph vo2MaxData={vO2MaxData} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && sp02IsSuccess && sp02Data && sp02Data.length > 0 && (
                    <Sp02HistoryGraph sp02Data={sp02Data} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && bpIsSuccess && bpData && bpData.length > 0 && (
                    <BloodPressureHistoryGraph bpData={bpData} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && ecgListIsSuccess && ecgList && ecgList.length > 0 && (
                    <ECGMeasurements ecgList={ecgList} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && tempsIsSuccess && tempsData && tempsData.length > 0 && (
                    <TempHistoryGraph tempsData={tempsData} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && stepsIsSuccess && stepsData && stepsData.length > 0 && (
                    <StepsHistoryGraph stepsData={stepsData} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && biaIsSuccess && biaData && biaData.length > 0 && (
                    <BiaGraph biaData={biaData} patientInfo={patient.info} date={date} onDateChange={setDate} />
                )}

                {patientIsSuccess && (
                    <HealthUserEvents
                        userGuid={userId}
                        onEventClick={(eventInfo) => setDate(new Date(eventInfo.event.extendedProps.eventDto.timestamp * 1000))}
                    />
                )}
            </div>
        </>
    );

    return (
        <PageLayout
            pageTitle={t("health:menu.data_ext")}
            isLoadingContent={patientIsFetching || hrIsFetching || stepsIsFetching || usersIsFetching}
            onPageRefresh={handleRefresh}
            bodyContent={content}
        />
    );
};

export default HealthHistory;
